import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { SiteSettings } from '../types';

interface BotoxHobokenProps {
  settings: SiteSettings;
}

const BotoxHoboken: React.FC<BotoxHobokenProps> = ({ settings }) => {
  return (
    <div className="bg-theme-bg pt-32 pb-24 transition-colors">
      <Helmet>
        <title>Botox Hoboken | Esthetische Geneeskunde Dr. An De Cuyper</title>
        <meta name="description" content="Botox behandeling in regio Hoboken voor een frisse, natuurlijke uitstraling. Persoonlijk consult, veilige aanpak en subtiele resultaten." />
        <link rel="canonical" href="https://andecuyper.be/botox-hoboken" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "BreadcrumbList",
                "@id": "https://andecuyper.be/botox-hoboken#breadcrumb",
                "itemListElement": [
                  {
                    "@type": "ListItem",
                    "position": 1,
                    "name": "Home",
                    "item": "https://andecuyper.be/"
                  },
                  {
                    "@type": "ListItem",
                    "position": 2,
                    "name": "Botox Hoboken",
                    "item": "https://andecuyper.be/botox-hoboken"
                  }
                ]
              },
              {
                "@type": "FAQPage",
                "@id": "https://andecuyper.be/botox-hoboken#faq",
                "mainEntity": [
                  {
                    "@type": "Question",
                    "name": "Wat doet Botox precies voor mijn rimpels in Hoboken?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "Botox (botulinetoxine) zorgt voor het selectief en tijdelijk ontspannen van specifieke mimische gelaatsspieren. Hierdoor krijgt de bovenliggende huid de kans om tot rust te komen, waardoor bestaande rimpels (zoals frons- en voorhoofdsrimpels) verzachten en nieuwe rimpels effectief worden voorkomen."
                    }
                  },
                  {
                    "@type": "Question",
                    "name": "Is een Botox behandeling pijnlijk?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "Nee, de meeste mensen ervaren de behandeling niet als pijnlijk. We gebruiken uiterst fijne micronaalden (de dunste op de cosmetische markt), vergelijkbaar met acupunctuurnaaldjes. De injecties voelen aan als kleine, milde speldenprikjes die slechts enkele seconden duren."
                    }
                  },
                  {
                    "@type": "Question",
                    "name": "Hoe lang herstel ik van een Botox sessie?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "Er is vrijwel geen 'downtime' of hersteltijd. U kunt direct na uw afspraak in Hoboken uw dagelijkse bezigheden hervatten. De lichte rode bultjes ter hoogte van de injectieplaats trekken er meestal binnen de 15 tot 30 minuten volledig uit."
                    }
                  },
                  {
                    "@type": "Question",
                    "name": "Kan ik Botox combineren met fillers in Hoboken?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "Zeker. Botox en hyaluronzuur fillers vullen elkaar perfect aan. Terwijl Botox actieve spieren ontspant (dynamische rimpels), herstellen fillers volume en vullen ze hardnekkige, diepe statische plooien op. Dr. An De Cuyper adviseert u graag over deze gecombineerde aanpak tijdens het consult."
                    }
                  },
                  {
                    "@type": "Question",
                    "name": "Wat zijn de risico's van Botox behandelingen?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "Wanneer Botox wordt toegediend door een ervaren arts met diepgaande chirurgische anatomiekennis, zijn de risico's op complicaties zoals asymmetrie of een hangend ooglid uiterst minimaal. Dr. An De Cuyper werkt uitsluitend met klinisch bewezen, FDA-goedgekeurde A-merken voor maximale patiëntveiligheid."
                    }
                  }
                ]
              }
            ]
          })}
        </script>
      </Helmet>

      <section className="max-w-4xl mx-auto px-6">
        <span className="text-primary text-xs font-black uppercase tracking-[0.4em] mb-4 block">Hoboken en Regio Antwerpen</span>
        <h1 className="font-display text-5xl md:text-7xl text-theme-text leading-tight mb-12 animate-fade-in">
          Botox in Hoboken <br/>
          <span className="text-theme-muted italic">met een natuurlijke aanpak</span>
        </h1>

        <div className="prose prose-lg prose-neutral dark:prose-invert max-w-none text-theme-muted font-light leading-relaxed space-y-8">
          <p className="text-xl text-theme-text font-medium leading-normal italic border-l-4 border-primary pl-8">
            Wenst u uw expressie zachtjes te ontspannen en rimpels subtiel te laten vervagen met een veilige en professionele aanpak? Dr. An De Cuyper is uw vertrouwde arts voor een natuurlijke Botox behandeling in Hoboken.
          </p>

          <div className="my-8 flex flex-wrap gap-4">
            <a 
              href={settings.bookingUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-primary text-black px-8 py-4 rounded-xl font-black uppercase tracking-widest text-[10px] hover:bg-white transition-all inline-block"
            >
              Plan een persoonlijk consult
            </a>
            <Link 
              to="/tarieven"
              className="border border-theme-border text-theme-text px-8 py-4 rounded-xl font-black uppercase tracking-widest text-[10px] hover:bg-theme-surface transition-all inline-block"
            >
              Bekijk tarieven
            </Link>
          </div>

          <h2 className="text-3xl font-display text-theme-text pt-8">Doeltreffende rimpelbehandeling in regio Hoboken</h2>
          <p>
            Onze gezichtsspieren zijn de hele dag door hard aan het werk bij het lachen, praten en fronsen. Hierdoor ontstaan er met de jaren lijntjes in de huid die permanent zichtbaar kunnen blijven. Gevestigd in de Antwerpse regio, biedt Dr. An De Cuyper een medisch specialistische Botox-ervaring. We richten ons op het herstellen van een rustige, frisse en jeugdige uitstraling op een uiterst discrete en verantwoorde wijze.
          </p>
          <p>
            Voor patiënten uit Hoboken, Wilrijk, Antwerpen-Kiel en omliggende zones is onze praktijk makkelijk bereikbaar voor preventieve en corrigerende esthetische behandelingen.
          </p>

          <h2 className="text-3xl font-display text-theme-text pt-8">Esthetische visie: Harmonieus & Veilig</h2>
          <p>
            Bij MD An De Cuyper geloven we sterk in het behouden van uw unieke uitstraling. Een gezicht zonder enige expressie is niet onze doelstelling. We streven naar een resultaat waarbij u er simpelweg goed uitgerust uitziet. Door te kiezen voor kleine en uiterst selectieve doses botulinetoxine (de zogenaamde 'Baby Botox' benadering) herstellen we harmonie en souplesse in het gezicht.
          </p>

          <h2 className="text-3xl font-display text-theme-text pt-8">De voordelen van Dr. An De Cuyper</h2>
          <p>
            Esthetische geneeskunde is een vak apart dat verfijnde chirurgische precisie vereist. Dankzij haar rijke medische en chirurgische achtergrond kan Dr. An De Cuyper de onderliggende gelaatspieren heel secuur in kaart brengen.
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Professionele gelaatsanalyse:</strong> Een behandelplan op maat waarin we rekening houden met uw individuele anatomie.</li>
            <li><strong>Optimale nazorg:</strong> We begeleiden u ook na de behandeling intensief en voorzien altijd een controle-evaluatie.</li>
            <li><strong>Bewezen A-merken:</strong> Er wordt uitsluitend gewerkt met gecertificeerde, veilige en biologisch afbreekbare materialen.</li>
          </ul>

          <div className="my-8 p-8 bg-theme-surface rounded-2xl border border-theme-border text-center">
            <p className="text-lg text-theme-text font-medium mb-4">Wenst u meer informatie over Botox of andere behandelingen?</p>
            <a 
              href={settings.bookingUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-primary text-black px-8 py-4 rounded-xl font-bold uppercase tracking-wider text-xs hover:bg-white transition-all inline-block"
            >
              Vraag advies aan Dr. An De Cuyper
            </a>
          </div>

          <h2 className="text-3xl font-display text-theme-text pt-8">Behandelingsprocedure in Hoboken</h2>
          <p>
            Een typische afspraak omvat de volgende stappen om een maximaal resultaat en een comfortabele beleving te garanderen:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Medisch consult:</strong> Bespreking van uw medische geschiedenis en verkenning van uw esthetische wensen.</li>
            <li><strong>Analyse van spierkracht:</strong> We vragen u te fronsen of te lachen om de spierspanning nauwgezet te lokaliseren.</li>
            <li><strong>De micro-injecties:</strong> Gevoeligheid verminderen we door te werken met de dunste micro-naaltjes op de markt.</li>
            <li><strong>Advies na de behandeling:</strong> We geven u specifieke instructies mee (onder andere 4 uur niet gaan liggen of bukken, 24 uur geen zware inspanning of hitte-blootstelling).</li>
          </ul>

          <h2 className="text-3xl font-display text-theme-text pt-8">Veelgestelde Vragen over Botox (FAQ)</h2>
          <div className="space-y-6 not-prose mt-8">
            <div className="bg-theme-surface p-8 rounded-2xl border border-theme-border">
              <h3 className="font-bold text-lg text-theme-text mb-2">1. Wat doet Botox precies voor mijn rimpels in Hoboken?</h3>
              <p className="text-sm opacity-80 text-theme-muted">Botox (botulinetoxine) zorgt voor het selectief en tijdelijk ontspannen van specifieke mimische gelaatsspieren. Hierdoor krijgt de bovenliggende huid de kans om tot rust te komen, waardoor bestaande rimpels (zoals frons- en voorhoofdsrimpels) verzachten en nieuwe rimpels effectief worden voorkomen.</p>
            </div>
            <div className="bg-theme-surface p-8 rounded-2xl border border-theme-border">
              <h3 className="font-bold text-lg text-theme-text mb-2">2. Is een Botox behandeling pijnlijk?</h3>
              <p className="text-sm opacity-80 text-theme-muted">Nee, de meeste mensen ervaren de behandeling niet als pijnlijk. We gebruiken uiterst fijne micronaalden (de dunste op de cosmetische markt), vergelijkbaar met acupunctuurnaaldjes. De injecties voelen aan als kleine, milde speldenprikjes die slechts enkele seconden duren.</p>
            </div>
            <div className="bg-theme-surface p-8 rounded-2xl border border-theme-border">
              <h3 className="font-bold text-lg text-theme-text mb-2">3. Hoe lang herstel ik van een Botox sessie?</h3>
              <p className="text-sm opacity-80 text-theme-muted">Er is vrijwel geen &#x27;downtime&#x27; of hersteltijd. U kunt direct na uw afspraak in Hoboken uw dagelijkse bezigheden hervatten. De lichte rode bultjes ter hoogte van de injectieplaats trekken er meestal binnen de 15 tot 30 minuten volledig uit.</p>
            </div>
            <div className="bg-theme-surface p-8 rounded-2xl border border-theme-border">
              <h3 className="font-bold text-lg text-theme-text mb-2">4. Kan ik Botox combineren met fillers in Hoboken?</h3>
              <p className="text-sm opacity-80 text-theme-muted">Zeker. Botox en hyaluronzuur fillers vullen elkaar perfect aan. Terwijl Botox actieve spieren ontspant (dynamische rimpels), herstellen fillers volume en vullen ze hardnekkige, diepe statische plooien op. Dr. An De Cuyper adviseert u graag over deze gecombineerde aanpak tijdens het consult.</p>
            </div>
            <div className="bg-theme-surface p-8 rounded-2xl border border-theme-border">
              <h3 className="font-bold text-lg text-theme-text mb-2">5. Wat zijn de risico's van Botox behandelingen?</h3>
              <p className="text-sm opacity-80 text-theme-muted">Wanneer Botox wordt toegediend door een ervaren arts met diepgaande chirurgische anatomiekennis, zijn de risico&#x27;s op complicaties zoals asymmetrie of een hangend ooglid uiterst minimaal. Dr. An De Cuyper werkt uitsluitend met klinisch bewezen, FDA-goedgekeurde A-merken voor maximale patiëntveiligheid.</p>
            </div>
          </div>

          <div className="text-xs text-theme-muted mt-12 pt-6 border-t border-theme-border flex flex-col sm:flex-row justify-between gap-4">
            <span>Locatie: <Link to="/praktijk-hoboken" className="underline hover:text-primary">Praktijk Hoboken</Link></span>
            <span>Navigeer direct: <Link to="/botox-antwerpen" className="underline hover:text-primary">Botox Antwerpen</Link> | <Link to="/fillers-antwerpen" className="underline hover:text-primary">Fillers Antwerpen</Link> | <Link to="/tarieven" className="underline hover:text-primary">Tarieven</Link></span>
          </div>

          <div className="mt-16 bg-background-dark p-12 rounded-[3rem] text-center text-white border border-white/5">
            <h3 className="font-display text-4xl mb-6">Plan uw consultatie in Hoboken</h3>
            <p className="mb-10 text-gray-400 max-w-xl mx-auto">
              Plan vandaag nog uw afspraak bij Dr. An De Cuyper en ervaar esthetische zorg van een medisch specialist.
            </p>
            <a 
              href={settings.bookingUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-primary text-black px-12 py-5 rounded-2xl font-black uppercase tracking-widest text-xs hover:bg-white transition-all inline-block"
            >
              Maak een afspraak
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BotoxHoboken;
